import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Layout from "../components/Layout";
import { api } from "../services/api";
import { CheckCircle, XCircle, Clock, CreditCard, ArrowLeft } from "lucide-react";

interface EstadoPago {
  estado: string;
  referencia: string;
  valor: number;
  concepto_label: string;
  concepto_detalle: string | null;
  wompi_transaction_id: string | null;
  fecha: string | null;
}

const COP = (v: number) =>
  new Intl.NumberFormat("es-CO", {
    style: "currency", currency: "COP", maximumFractionDigits: 0,
  }).format(v);

export default function PagoResultadoPage() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [pago, setPago] = useState<EstadoPago | null>(null);
  const [loading, setLoading] = useState(true);
  const [intentos, setIntentos] = useState(0);

  // Wompi redirige con ?id={transaction_id} y normalmente también ref
  const wompiId  = params.get("id");
  const refParam = params.get("ref") || params.get("reference");

  useEffect(() => {
    if (!refParam && !wompiId) {
      setLoading(false);
      return;
    }

    // Polleamos máximo 5 veces esperando que el webhook actualice el estado
    const fetchEstado = async () => {
      if (!refParam) return;
      try {
        const r = await api.get(`/wompi/estado/${refParam}`);
        setPago(r.data);
        // Si sigue PENDING y no hemos llegado al máximo, reintentar
        if (r.data.estado === "PENDING" && intentos < 4) {
          setIntentos(p => p + 1);
          setTimeout(fetchEstado, 3000);
        }
      } catch {
        /* si no existe referencia mostramos el estado recibido de Wompi */
      } finally {
        setLoading(false);
      }
    };

    fetchEstado();
  }, [refParam]); // eslint-disable-line

  const estado = pago?.estado || (wompiId ? "PENDING" : "ERROR");

  const INFO: Record<string, {
    icon: React.ReactNode;
    titulo: string;
    descripcion: string;
    bg: string;
    border: string;
  }> = {
    APPROVED: {
      icon: <CheckCircle size={52} className="text-green-500" />,
      titulo: "¡Pago exitoso! 🎉",
      descripcion: "Tu pago fue procesado correctamente por Wompi. La academia ya tiene registro de tu transacción.",
      bg: "bg-green-50",
      border: "border-green-200",
    },
    PENDING: {
      icon: <Clock size={52} className="text-yellow-500" />,
      titulo: "Pago en proceso",
      descripcion: "Tu pago está siendo verificado. Esto puede tardar unos minutos. Te notificaremos cuando se confirme.",
      bg: "bg-yellow-50",
      border: "border-yellow-200",
    },
    DECLINED: {
      icon: <XCircle size={52} className="text-red-500" />,
      titulo: "Pago declinado",
      descripcion: "Tu pago fue rechazado. Podés intentarlo de nuevo con otro método de pago o verificar los datos de tu tarjeta.",
      bg: "bg-red-50",
      border: "border-red-200",
    },
    VOIDED: {
      icon: <XCircle size={52} className="text-gray-400" />,
      titulo: "Pago anulado",
      descripcion: "La transacción fue anulada. Podés generar un nuevo pago si lo necesitás.",
      bg: "bg-gray-50",
      border: "border-gray-200",
    },
    ERROR: {
      icon: <XCircle size={52} className="text-red-500" />,
      titulo: "Error en el pago",
      descripcion: "Ocurrió un error al procesar el pago. Contactá a la academia si el dinero fue debitado.",
      bg: "bg-red-50",
      border: "border-red-200",
    },
  };

  const info = INFO[estado] ?? INFO["PENDING"];

  return (
    <Layout>
      <div className="p-6 max-w-2xl mx-auto">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-24 gap-4">
            <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
            <p className="text-gray-500 text-sm">Verificando estado del pago…</p>
          </div>
        ) : (
          <div className={`rounded-2xl border ${info.border} ${info.bg} p-8 text-center`}>
            <div className="flex justify-center mb-4">{info.icon}</div>
            <h1 className="text-2xl font-extrabold text-[#0D1B2A] mb-2">{info.titulo}</h1>
            <p className="text-gray-600 text-sm mb-6 max-w-md mx-auto">{info.descripcion}</p>

            {pago && (
              <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6 text-left space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Concepto</span>
                  <span className="font-semibold text-[#0D1B2A]">{pago.concepto_label}</span>
                </div>
                {pago.concepto_detalle && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Detalle</span>
                    <span className="font-semibold text-[#0D1B2A]">{pago.concepto_detalle}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm border-t border-gray-100 pt-2">
                  <span className="font-bold text-[#0D1B2A]">Total</span>
                  <span className="font-extrabold text-blue-700 text-base">{COP(pago.valor)}</span>
                </div>
                <div className="flex justify-between text-xs text-gray-400 pt-1">
                  <span>Referencia</span>
                  <code className="font-mono">{pago.referencia}</code>
                </div>
                {pago.wompi_transaction_id && (
                  <div className="flex justify-between text-xs text-gray-400">
                    <span>ID Wompi</span>
                    <code className="font-mono">{pago.wompi_transaction_id}</code>
                  </div>
                )}
              </div>
            )}

            {!pago && (wompiId || refParam) && (
              <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6 text-left">
                {wompiId && (
                  <div className="flex justify-between text-xs text-gray-400">
                    <span>ID Wompi</span>
                    <code className="font-mono">{wompiId}</code>
                  </div>
                )}
                {refParam && (
                  <div className="flex justify-between text-xs text-gray-400">
                    <span>Referencia</span>
                    <code className="font-mono">{refParam}</code>
                  </div>
                )}
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={() => nav("/pago-online")}
                className="flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-bold py-2.5 px-5 rounded-xl text-sm transition"
              >
                <CreditCard size={15} />
                Realizar otro pago
              </button>
              <button
                onClick={() => nav("/dashboard")}
                className="flex items-center justify-center gap-2 border border-gray-200 text-gray-600 hover:bg-gray-50 font-medium py-2.5 px-5 rounded-xl text-sm transition"
              >
                <ArrowLeft size={15} />
                Ir al Dashboard
              </button>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
